import React, { useEffect, useState } from "react";
import { Plus, Factory, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { Modal, Field, inputCls, Th, Td, Empty, fmtDate } from "@/pages/admin/AdminUI";

const empty = {
  paddy_quantity: 0, paddy_type: "ব্রি-২৯", paddy_cost_per_kg: 0, moisture: 0,
  machine: "মেশিন-১", operator: "", labour_cost: 0, fuel_cost: 0, other_cost: 0,
  out_rice: 0, out_broken: 0, out_bran: 0, out_husk: 0, out_waste: 0,
};

export default function AdminProduction() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/production").then((r) => setBatches(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalOut = ["out_rice", "out_broken", "out_bran", "out_husk", "out_waste"].reduce((s, k) => s + Number(form[k] || 0), 0);
  const inp = Number(form.paddy_quantity) || 0;
  const recovery = inp ? ((Number(form.out_rice) || 0) / inp * 100).toFixed(1) : 0;

  const save = async () => {
    const payload = { ...form };
    Object.keys(payload).forEach((k) => { if (typeof empty[k] === "number") payload[k] = Number(payload[k]) || 0; });
    try { await api.post("/admin/production", payload); toast.success("প্রোডাকশন ব্যাচ সংরক্ষিত হয়েছে"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B] flex items-center gap-2"><Factory /> প্রোডাকশন ম্যানেজমেন্ট</h1>
        <button onClick={() => { setForm(empty); setOpen(true); }} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="add-production-btn"><Plus size={18} /> নতুন প্রোডাকশন</button>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-stone-50 border-b border-stone-200"><tr><Th>ব্যাচ নং</Th><Th>তারিখ</Th><Th>ধান (কেজি)</Th><Th>চাল (কেজি)</Th><Th>রিকভারি %</Th><Th>খরচ/কেজি</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {batches.map((b) => (
              <tr key={b.id} data-testid={`batch-row-${b.batch_no}`}>
                <Td className="font-semibold text-[#1E3A2B]">{b.batch_no}</Td>
                <Td>{fmtDate(b.date)}</Td>
                <Td>{b.paddy_quantity}</Td>
                <Td>{b.outputs.rice}</Td>
                <Td><span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-semibold">{b.rice_recovery_pct}%</span></Td>
                <Td>{taka(b.cost_per_kg_rice)}</Td>
                <Td><button onClick={() => setView(b)} className="p-2 text-blue-600" data-testid={`view-batch-${b.batch_no}`}><Eye size={16} /></button></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {batches.length === 0 && <Empty text="কোনো প্রোডাকশন রেকর্ড নেই" />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="নতুন প্রোডাকশন ব্যাচ" wide>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="ধানের পরিমাণ (কেজি)"><input type="number" className={inputCls} value={form.paddy_quantity} onChange={(e) => set("paddy_quantity", e.target.value)} data-testid="prod-paddy-qty" /></Field>
          <Field label="ধানের ধরন"><input className={inputCls} value={form.paddy_type} onChange={(e) => set("paddy_type", e.target.value)} /></Field>
          <Field label="ধান দর (৳/কেজি)"><input type="number" className={inputCls} value={form.paddy_cost_per_kg} onChange={(e) => set("paddy_cost_per_kg", e.target.value)} /></Field>
          <Field label="আর্দ্রতা %"><input type="number" className={inputCls} value={form.moisture} onChange={(e) => set("moisture", e.target.value)} /></Field>
          <Field label="মেশিন"><input className={inputCls} value={form.machine} onChange={(e) => set("machine", e.target.value)} /></Field>
          <Field label="অপারেটর"><input className={inputCls} value={form.operator} onChange={(e) => set("operator", e.target.value)} /></Field>
        </div>
        <div className="mt-3 font-semibold text-[#1E3A2B]">আউটপুট (কেজি)</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-1">
          <Field label="চাল"><input type="number" className={inputCls} value={form.out_rice} onChange={(e) => set("out_rice", e.target.value)} data-testid="prod-out-rice" /></Field>
          <Field label="খুদ"><input type="number" className={inputCls} value={form.out_broken} onChange={(e) => set("out_broken", e.target.value)} /></Field>
          <Field label="ব্রান"><input type="number" className={inputCls} value={form.out_bran} onChange={(e) => set("out_bran", e.target.value)} /></Field>
          <Field label="তুষ"><input type="number" className={inputCls} value={form.out_husk} onChange={(e) => set("out_husk", e.target.value)} /></Field>
          <Field label="ওয়েস্ট"><input type="number" className={inputCls} value={form.out_waste} onChange={(e) => set("out_waste", e.target.value)} /></Field>
        </div>
        <div className="mt-3 font-semibold text-[#1E3A2B]">খরচ (৳)</div>
        <div className="grid grid-cols-3 gap-3 mt-1">
          <Field label="শ্রমিক"><input type="number" className={inputCls} value={form.labour_cost} onChange={(e) => set("labour_cost", e.target.value)} /></Field>
          <Field label="জ্বালানি"><input type="number" className={inputCls} value={form.fuel_cost} onChange={(e) => set("fuel_cost", e.target.value)} /></Field>
          <Field label="অন্যান্য"><input type="number" className={inputCls} value={form.other_cost} onChange={(e) => set("other_cost", e.target.value)} /></Field>
        </div>
        <div className="mt-3 bg-emerald-50 rounded-xl p-3 flex justify-between text-sm font-medium text-emerald-900">
          <span>মোট আউটপুট: {totalOut} কেজি</span>
          <span>চাল রিকভারি: {recovery}%</span>
        </div>
        <button onClick={save} className="mt-4 w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-production-btn">ব্যাচ সংরক্ষণ</button>
      </Modal>

      <Modal open={!!view} onClose={() => setView(null)} title={view?.batch_no}>
        {view && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Info label="তারিখ" value={fmtDate(view.date)} /><Info label="ধান" value={`${view.paddy_quantity} কেজি`} />
              <Info label="মেশিন" value={view.machine} /><Info label="অপারেটর" value={view.operator || "-"} />
              <Info label="ধান খরচ" value={taka(view.paddy_cost)} /><Info label="মোট খরচ" value={taka(view.total_cost)} />
            </div>
            <div className="border-t border-stone-200 pt-2 font-semibold text-[#1E3A2B]">আউটপুট ও রিকভারি</div>
            {[["চাল", view.outputs.rice, view.rice_recovery_pct], ["খুদ", view.outputs.broken, view.broken_pct], ["ব্রান", view.outputs.bran, view.bran_pct], ["তুষ", view.outputs.husk, view.husk_pct], ["ওয়েস্ট", view.outputs.waste, view.waste_pct]].map(([l, q, p]) => (
              <div key={l} className="flex justify-between"><span>{l}</span><span className="font-medium">{q} কেজি ({p}%)</span></div>
            ))}
            <div className="flex justify-between font-bold text-emerald-800 border-t border-stone-200 pt-2"><span>মোট রিকভারি</span><span>{view.recovery_pct}%</span></div>
            <div className="flex justify-between font-bold text-[#1E3A2B]"><span>চাল প্রতি কেজি খরচ</span><span>{taka(view.cost_per_kg_rice)}</span></div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const Info = ({ label, value }) => <div className="bg-stone-50 rounded-lg p-2"><div className="text-xs text-stone-400">{label}</div><div className="font-medium">{value}</div></div>;
